import React, { useEffect, useState } from "react";
import {
  Bell,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  Clock,
  Database,
  FileText,
  GalleryHorizontal,
  Gift,
  GitBranch,
  Globe,
  LifeBuoy,
  ListChecks,
  Mail,
  MessageSquare,
  Plus,
  Radio,
  Tag,
  Timer,
  Users,
  UserPlus,
  Variable,
  XCircle,
} from "lucide-react";
import { ActionNode } from "../../../../views/convoauto-pages/bot-studio/types";
import { LeftDot, RightDot } from "../../../shared/action-core";
import useRequest from "../../../hook/use-req";
import type { TemplateModule } from "../../../../views/marketing-pages/templates/types";
import { RcsTemplateCreator } from "./rcs-template-creator";
import { extractTemplatePreviewImage } from "./rcs-actions";

const RCS_ICON: Record<string, React.ElementType> = {
  "Send RCS Template": FileText,
  "Send Menu": ListChecks,
  "Send Carousel": GalleryHorizontal,
  "Send Text": MessageSquare,
  "Wait for Reply": Timer,
  "Set Variable": Variable,
  "Escalate to Human": UserPlus,
  "Confirm Order": CheckCircle2,
  "Branch": GitBranch,
  // WhatsApp / normal Bot Studio action types
  "Send Message": MessageSquare,
  "Send Interactive Message": MessageSquare,
  "Send Message Template": FileText,
  "WhatsApp Message": MessageSquare,
  "Send Email": Mail,
  "Send Birthday Message": Gift,
  "Set Delay Time": Clock,
  "Wait Till": Timer,
  "Wait Until": Timer,
  "Run at Specific Time": CalendarClock,
  "Fetch Current Date": CalendarDays,
  "Fetch Audience": Users,
  "Set Customer Attribute": Tag,
  "Condition": GitBranch,
  "Mark as Sent": CheckCircle2,
  "Store Data": Database,
  "Create Support Ticket": LifeBuoy,
  "Send Internal Notification": Bell,
  "Assign Agent": UserPlus,
  "Add or Remove Tags": Tag,
  "Close Conversation": XCircle,
  "Call API": Globe,
  "Add to Broadcast": Radio,
};

// Apple-Shortcuts-style verb/color per action type. Instead of every
// canvas card labelled "ACTION" in grey, group actions into a small
// set of colored verbs so the flow reads at a glance: green = send,
// grey = if/condition, amber = wait, slate = data/variable, red = end,
// indigo = everything else. Matches Joseph 2026-08-04 "make use of
// something like [Apple Shortcuts]".
const ACTION_VERB: Record<string, { verb: string; iconBg: string; iconColor: string }> = {
  // Send verbs — green
  "Send RCS Template":        { verb: "SEND",     iconBg: "bg-green-500",  iconColor: "text-white" },
  "Send Menu":                { verb: "SEND",     iconBg: "bg-green-500",  iconColor: "text-white" },
  "Send Carousel":            { verb: "SEND",     iconBg: "bg-green-500",  iconColor: "text-white" },
  "Send Text":                { verb: "SEND",     iconBg: "bg-green-500",  iconColor: "text-white" },
  "Send Message":             { verb: "SEND",     iconBg: "bg-green-500",  iconColor: "text-white" },
  "Send Interactive Message": { verb: "SEND",     iconBg: "bg-green-500",  iconColor: "text-white" },
  "Send Message Template":    { verb: "SEND",     iconBg: "bg-green-500",  iconColor: "text-white" },
  "WhatsApp Message":         { verb: "SEND",     iconBg: "bg-green-500",  iconColor: "text-white" },
  // This node is the "pick channel" step at the top of a New Lead
  // journey — it now hosts a Channel dropdown (Email / WhatsApp) so
  // the canvas verb reads SELECT CHANNEL rather than SEND. The card
  // does still trigger the send downstream; the verb reflects the
  // step's purpose from the operator's POV. (Joseph 2026-08-04
  // "the first send where we pick channel change it to select
  // channel instead of send")
  "Send Email":               { verb: "SELECT CHANNEL", iconBg: "bg-indigo-500", iconColor: "text-white" },
  "Send Birthday Message":    { verb: "SEND",     iconBg: "bg-green-500",  iconColor: "text-white" },
  "Send Internal Notification": { verb: "NOTIFY", iconBg: "bg-purple-500", iconColor: "text-white" },
  // Condition — Shortcuts grey Y
  "Condition":                { verb: "IF",       iconBg: "bg-gray-500",   iconColor: "text-white" },
  "Branch":                   { verb: "IF",       iconBg: "bg-gray-500",   iconColor: "text-white" },
  // Wait verbs — amber
  "Wait for Reply":           { verb: "WAIT",     iconBg: "bg-amber-500",  iconColor: "text-white" },
  "Set Delay Time":           { verb: "WAIT",     iconBg: "bg-amber-500",  iconColor: "text-white" },
  "Wait Till":                { verb: "WAIT",     iconBg: "bg-amber-500",  iconColor: "text-white" },
  "Wait Until":               { verb: "WAIT",     iconBg: "bg-amber-500",  iconColor: "text-white" },
  "Run at Specific Time":     { verb: "SCHEDULE", iconBg: "bg-amber-500",  iconColor: "text-white" },
  // Data/variable — slate
  "Set Variable":             { verb: "SET",      iconBg: "bg-slate-600",  iconColor: "text-white" },
  "Set Customer Attribute":   { verb: "SET",      iconBg: "bg-slate-600",  iconColor: "text-white" },
  "Store Data":               { verb: "STORE",    iconBg: "bg-slate-600",  iconColor: "text-white" },
  "Fetch Current Date":       { verb: "FETCH",    iconBg: "bg-slate-600",  iconColor: "text-white" },
  "Fetch Audience":           { verb: "FETCH",    iconBg: "bg-slate-600",  iconColor: "text-white" },
  "Call API":                 { verb: "CALL",     iconBg: "bg-slate-600",  iconColor: "text-white" },
  "Add to Broadcast":         { verb: "BROADCAST",iconBg: "bg-slate-600",  iconColor: "text-white" },
  // Terminals — red
  "Confirm Order":            { verb: "CONFIRM",  iconBg: "bg-red-500",    iconColor: "text-white" },
  "Escalate to Human":        { verb: "ESCALATE", iconBg: "bg-red-500",    iconColor: "text-white" },
  "Assign Agent":             { verb: "ASSIGN",   iconBg: "bg-red-500",    iconColor: "text-white" },
  "Close Conversation":       { verb: "CLOSE",    iconBg: "bg-red-500",    iconColor: "text-white" },
  "Mark as Sent":             { verb: "MARK",     iconBg: "bg-red-500",    iconColor: "text-white" },
  "Create Support Ticket":    { verb: "TICKET",   iconBg: "bg-red-500",    iconColor: "text-white" },
  "Add or Remove Tags":       { verb: "TAG",      iconBg: "bg-slate-600",  iconColor: "text-white" },
};

// Compact mini-picker rendered directly on the "Send RCS Template" canvas
// card — lets the operator pick a template without opening the sidebar,
// same as the old RCS Bot Studio's InlineTemplatePicker. Selecting a
// template still surfaces a thumbnail right here (extractTemplatePreviewImage).
const InlineTemplatePicker: React.FC<{
  templateId: string;
  onPick: (t: TemplateModule) => void;
}> = ({ templateId, onPick }) => {
  const token = localStorage.getItem("authToken");
  const { makeRequest: getTemplates, loading } = useRequest("/templates/", "GET", {
    Authorization: `Bearer ${token}`,
  });
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState<TemplateModule[]>([]);
  const [creatorOpen, setCreatorOpen] = useState(false);

  const reload = async () => {
    try {
      const [response] = await getTemplates(undefined, { page: 1, limit: 500 });
      const rows: TemplateModule[] = Array.isArray(response)
        ? response : Array.isArray((response as any)?.data) ? (response as any).data : [];
      setTemplates(rows.filter((t) => (t.channel || "").toLowerCase() === "rcs"));
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (!open || templates.length > 0) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div className="relative" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen((s) => !s)}
        className="w-full flex items-center justify-between gap-1.5 border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1.5 text-[11px] bg-white dark:bg-gray-900 hover:border-indigo-300"
      >
        <span className={`truncate ${templateId ? "text-gray-900 dark:text-gray-100 font-medium" : "text-[#7B7B7B] font-semibold dark:text-gray-500"}`}>
          {templateId || "Pick a template…"}
        </span>
        <span className="text-[#7B7B7B] font-semibold dark:text-gray-500">▾</span>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 left-0 right-0 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-y-auto">
          <button
            onClick={() => setCreatorOpen(true)}
            className="sticky top-0 z-10 w-full text-left px-2 py-2 text-[11px] bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold border-b border-indigo-100 flex items-center gap-1"
          >
            <Plus size={11} /> Create new template
          </button>
          {loading ? (
            <p className="px-2 py-2 text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">Loading…</p>
          ) : templates.length === 0 ? (
            <p className="px-2 py-2 text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">No templates yet.</p>
          ) : (
            templates.map((t) => (
              <button
                key={t.id}
                onClick={() => { onPick(t); setOpen(false); }}
                className={`w-full text-left px-2 py-1.5 text-[11px] hover:bg-gray-50 dark:hover:bg-gray-800 truncate ${
                  t.template_name === templateId ? "bg-indigo-50 text-indigo-700 font-medium" : "text-gray-700 dark:text-gray-300"
                }`}
              >
                {t.template_name}
              </button>
            ))
          )}
        </div>
      )}

      <RcsTemplateCreator
        open={creatorOpen}
        onClose={() => setCreatorOpen(false)}
        agentId=""
        onCreated={async (tpl) => { await reload(); onPick(tpl); }}
      />
    </div>
  );
};

interface RcsActionSummaryCardProps {
  node: ActionNode;
  selected: boolean;
  onSelect: () => void;
  onUpdate: (config: Record<string, any>) => void;
}

// Compact canvas-card summary for every RCS action type — click anywhere on
// the card (outside the template mini-picker) to open the full config in
// RcsConfigPanel on the right, matching the old RCS Bot Studio's NodeCard +
// ConfigPanel split.
export const RcsActionSummaryCard: React.FC<RcsActionSummaryCardProps> = ({ node, selected, onSelect, onUpdate }) => {
  const Icon = RCS_ICON[node.type] ?? FileText;
  const cfg = node.config || {};

  const pickTemplate = (t: TemplateModule) => {
    onUpdate({
      ...cfg,
      template_id: t.template_name,
      template_variables: Object.fromEntries(
        (t.variables || []).map((v: any) => [v.name, v.example || `{{${v.name}}}`]),
      ),
      replyText:
        t.content ||
        (Array.isArray((t as any).components)
          ? ((t as any).components.find((c: any) => c?.type === "text")?.text ?? "")
          : ""),
      template_preview_image: extractTemplatePreviewImage(t),
    });
  };

  const summary = () => {
    switch (node.type) {
      case "Send RCS Template":
        return (
          <div className="mt-2 space-y-1.5">
            <InlineTemplatePicker templateId={cfg.template_id || ""} onPick={pickTemplate} />
            {cfg.template_preview_image && (
              <img
                src={cfg.template_preview_image}
                alt=""
                className="w-full h-16 object-cover rounded-md border border-gray-200 dark:border-gray-700"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
              />
            )}
          </div>
        );
      case "Send Menu":
        return (
          <>
            {cfg.replyText && <p className="text-[11px] text-gray-600 dark:text-gray-300 line-clamp-2 mt-1">{cfg.replyText}</p>}
            <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">
              {(cfg.chips || []).length} chip{(cfg.chips || []).length === 1 ? "" : "s"}
            </p>
          </>
        );
      case "Send Carousel":
        return (
          <>
            {cfg.replyText && <p className="text-[11px] text-gray-600 dark:text-gray-300 line-clamp-2 mt-1">{cfg.replyText}</p>}
            <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">
              {(cfg.cards || []).length} card{(cfg.cards || []).length === 1 ? "" : "s"}
            </p>
          </>
        );
      case "Send Text":
      case "Confirm Order":
        return cfg.replyText ? (
          <p className="text-[11px] text-gray-600 dark:text-gray-300 line-clamp-2 mt-1">{cfg.replyText}</p>
        ) : (
          <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Click to add a reply message</p>
        );
      case "Wait for Reply":
        return <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Pauses for chip tap or free text</p>;
      case "Set Variable":
        return cfg.variable ? (
          <p className="text-[11px] text-gray-600 dark:text-gray-300 mt-1 font-mono">{cfg.variable} = {cfg.value || "—"}</p>
        ) : (
          <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Click to set a variable</p>
        );
      case "Escalate to Human":
        return cfg.transfer_reason ? (
          <p className="text-[11px] text-gray-600 dark:text-gray-300 mt-1">{cfg.transfer_reason}</p>
        ) : (
          <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Click to add a transfer reason</p>
        );
      case "Branch":
        return cfg.variable ? (
          <p className="text-[11px] text-gray-600 dark:text-gray-300 mt-1 font-mono">{cfg.variable} == {cfg.equals || "—"}</p>
        ) : (
          <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Click to configure the check</p>
        );
      case "Send Message":
      case "Send Interactive Message":
      case "WhatsApp Message":
        return cfg.message || cfg.templateName ? (
          <p className="text-[11px] text-gray-600 dark:text-gray-300 line-clamp-2 mt-1">{cfg.templateName || cfg.message}</p>
        ) : (
          <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Click to compose the message</p>
        );
      case "Send Message Template":
      case "Send Birthday Message":
      case "Send Email":
        // On the WhatsApp path there's no template picker on this card —
        // the operator picks the WhatsApp template on the downstream
        // send node after the condition branches.
        if (node.type === "Send Email" && cfg.channel === "WhatsApp") {
          return (
            <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">
              WhatsApp — template on downstream send node
            </p>
          );
        }
        return cfg.templateName || cfg.template ? (
          <p className="text-[11px] text-gray-600 dark:text-gray-300 mt-1">{cfg.templateName || cfg.template}</p>
        ) : (
          <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Click to pick a template</p>
        );
      case "Set Delay Time":
        return (
          <p className="text-[11px] text-gray-600 dark:text-gray-300 mt-1">
            {cfg.days ?? 0}d {cfg.hours ?? 0}h {cfg.minutes ?? 0}m
          </p>
        );
      case "Wait Till":
      case "Wait Until":
        return <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Pauses until a signal or date condition</p>;
      case "Run at Specific Time":
        return cfg.fixedTime ? (
          <p className="text-[11px] text-gray-600 dark:text-gray-300 mt-1">{cfg.fixedTime}</p>
        ) : (
          <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Click to set a schedule</p>
        );
      case "Fetch Current Date":
        return <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Fetches today's date</p>;
      case "Fetch Audience":
        return <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Click to configure recipients</p>;
      case "Set Customer Attribute":
        return cfg.attribute ? (
          <p className="text-[11px] text-gray-600 dark:text-gray-300 mt-1 font-mono">{cfg.attribute} = {cfg.value || "—"}</p>
        ) : (
          <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Click to choose an attribute</p>
        );
      case "Condition":
        return <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Click to configure the condition</p>;
      case "Mark as Sent":
        return <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Updates contact status</p>;
      case "Store Data":
        return <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Click to configure storage rules</p>;
      case "Create Support Ticket":
        return <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Click to configure the ticket</p>;
      case "Send Internal Notification":
        return cfg.team ? (
          <p className="text-[11px] text-gray-600 dark:text-gray-300 mt-1">Notifies {cfg.team}</p>
        ) : (
          <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Click to choose a team</p>
        );
      case "Assign Agent":
        return cfg.agentName ? (
          <p className="text-[11px] text-gray-600 dark:text-gray-300 mt-1">{cfg.agentName}</p>
        ) : (
          <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Click to pick an agent</p>
        );
      case "Add or Remove Tags":
        return (cfg.tags || []).length > 0 ? (
          <p className="text-[11px] text-gray-600 dark:text-gray-300 line-clamp-2 mt-1">{(cfg.tags || []).join(", ")}</p>
        ) : (
          <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Click to add tags</p>
        );
      case "Close Conversation":
        return <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Closes the current conversation</p>;
      case "Call API":
        return cfg.url ? (
          <p className="text-[11px] text-gray-600 dark:text-gray-300 line-clamp-1 mt-1 font-mono">{cfg.url}</p>
        ) : (
          <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Click to set the endpoint</p>
        );
      case "Add to Broadcast":
        return cfg.template ? (
          <p className="text-[11px] text-gray-600 dark:text-gray-300 mt-1">{cfg.template}</p>
        ) : (
          <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Click to pick a template</p>
        );
      default:
        return <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Click to configure</p>;
    }
  };

  return (
    <div
      onClick={onSelect}
      className={`relative bg-white dark:bg-gray-900 rounded-xl border shadow-sm p-3.5 w-[240px] cursor-pointer transition-colors ${
        selected ? "border-indigo-500 ring-1 ring-indigo-100" : "border-gray-200 dark:border-gray-700 hover:border-indigo-300"
      }`}
    >
      <LeftDot />
      <RightDot />
      {/* Shortcuts-style card head — verb chip + colored icon square.
          Instead of every card labelled "ACTION" in grey, show the
          category verb (SEND / IF / WAIT / SET / …) so the flow reads
          at a glance. Colored icon square mirrors Apple Shortcuts
          conventions (green for send, grey for if, amber for wait). */}
      {(() => {
        const meta = ACTION_VERB[node.type] ?? { verb: "ACTION", iconBg: "bg-indigo-500", iconColor: "text-white" };
        return (
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md ${meta.iconBg}`}>
              <Icon size={13} className={meta.iconColor} />
            </span>
            <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{meta.verb}</span>
          </div>
        );
      })()}
      {/* Send Email nodes now carry channel in config — reflect the pick
          on the canvas card so operators aren't confused by a card
          labelled "Send Email" that actually fires WhatsApp. */}
      <p className="text-indigo-600 font-semibold text-sm">
        {node.type === "Send Email"
          ? (node.config?.channel === "WhatsApp" ? "Send WhatsApp" : "Send Email")
          : node.type}
      </p>
      {summary()}
    </div>
  );
};